import { z } from 'zod';
import type { FilterValue, ListState, ResourceColumn } from './types.js';

export const DEFAULT_PAGE_SIZE = 25;
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

/** One value in the URL. Kept loose on purpose: a filter owns its own meaning. */
const filterValueSchema: z.ZodType<FilterValue> = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.array(z.string()),
  z.object({ from: z.string().optional(), to: z.string().optional() }),
  z.null(),
]);

/**
 * Every list view's search params. Validated here rather than per resource so a
 * hand-edited or stale URL degrades to a sane view instead of throwing.
 */
export const listSearchSchema = z.object({
  page: z.coerce.number().int().min(1).catch(1).default(1),
  pageSize: z.coerce
    .number()
    .int()
    .min(1)
    .max(200)
    .catch(DEFAULT_PAGE_SIZE)
    .default(DEFAULT_PAGE_SIZE),
  sort: z.string().optional(),
  order: z.enum(['asc', 'desc']).catch('asc').default('asc'),
  q: z.string().optional(),
  filters: z.record(z.string(), filterValueSchema).catch({}).default({}),
});

export type ListSearch = z.infer<typeof listSearchSchema>;

export function defaultListState(overrides?: Partial<ListState>): ListState {
  return {
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    order: 'asc',
    filters: {},
    ...overrides,
  };
}

/**
 * `Array.isArray` does not narrow a `readonly string[]` out of the union, so the
 * two structured filter shapes get explicit guards.
 */
export function isMultiValue(value: FilterValue): value is readonly string[] {
  return Array.isArray(value);
}

export function isRangeValue(value: FilterValue): value is { from?: string; to?: string } {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** A filter is applied when it is neither null nor an empty string/array/range. */
export function isFilterApplied(value: FilterValue): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (isMultiValue(value)) return value.length > 0;
  if (isRangeValue(value)) return Boolean(value.from ?? value.to);
  return true;
}

export function appliedFilters(state: ListState): Record<string, FilterValue> {
  return Object.fromEntries(
    Object.entries(state.filters).filter(([, value]) => isFilterApplied(value)),
  );
}

export function activeFilterCount(state: ListState): number {
  return Object.keys(appliedFilters(state)).length;
}

/**
 * Any change other than paging returns to page 1: staying on page 7 of a result
 * set that just shrank to two pages is the classic way to show a user an empty
 * table and let them conclude the filter is broken.
 */
export function applyStateChange(state: ListState, patch: Partial<ListState>): ListState {
  const next = { ...state, ...patch };
  const pagingOnly =
    Object.keys(patch).length > 0 &&
    Object.keys(patch).every((key) => key === 'page' || key === 'pageSize');
  if (!pagingOnly) next.page = 1;
  return next;
}

/** Cycles asc → desc → unsorted, so a user can undo a sort with the same click. */
export function toggleSort(state: ListState, sortKey: string): ListState {
  if (state.sort !== sortKey) return applyStateChange(state, { sort: sortKey, order: 'asc' });
  if (state.order === 'asc') return applyStateChange(state, { sort: sortKey, order: 'desc' });
  return applyStateChange(state, { sort: undefined, order: 'asc' });
}

export function sortKeyOf<TItem>(column: ResourceColumn<TItem>): string | undefined {
  return column.sortKey;
}

export function pageCount(total: number, pageSize: number): number {
  return Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
}
